'use client';

import { faAngleDown, faAngleUp, faCheck, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, ModalContent } from '@pickleballinc/react-ui';
import { useEffect, useState } from 'react';

import { AllSessions } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import moment from 'moment';
import { allSessionsLinks } from '@/modules/league/new-components/constants';
import { useGetSessionsAllGameDaysQueryData } from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/queries';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';

interface OpenEnterScoreModal {
  modalOpened: boolean;
  setIsModalOpen: (show: boolean) => void;
  matchDateId?: string;
  sessionId: string;
  slug: string;
  leagueId: string;
}

const AllGameDaysModal = ({
  modalOpened,
  setIsModalOpen,
  sessionId,
  matchDateId,
  slug,
  leagueId,
}: OpenEnterScoreModal) => {
  const { data, isLoading } = useGetSessionsAllGameDaysQueryData(leagueId);
  const allSessions = data?.allSessions || { currentSession: [], pastSessions: [] };

  const [selectedTab, setSelectedTab] = useState<'current' | 'past'>('current');
  const [openedSessionIndices, setOpenedSessionIndices] = useState<number[]>([]);
  const { setLoading } = useLoader();

  const sessionsToShow =
    selectedTab === 'past' ? allSessions.pastSessions || [] : allSessions.currentSession || [];

  useEffect(() => {
    setOpenedSessionIndices([]);
  }, [selectedTab]);

  const toggleSession = (sessionIndex: number) => {
    setOpenedSessionIndices((prev) =>
      prev.includes(sessionIndex)
        ? prev.filter((index) => index !== sessionIndex)
        : [...prev, sessionIndex]
    );
  };

  // For current sessions, we want the one with LeagueSessionID equal to sessionId to be open by default
  const isSessionOpened = (session: AllSessions, sessionIndex: number) => {
    if (selectedTab === 'current' && sessionsToShow.length === 1) {
      return true;
    }
    if (selectedTab === 'current' && sessionsToShow.length > 1) {
      if (session.LeagueSessionID.toLowerCase() === sessionId.toLowerCase()) return true;
    }
    return openedSessionIndices.includes(sessionIndex);
  };

  if (!modalOpened) return null;

  const getLinkClass = (type: string) =>
    `flex-1 border-b-[5px] px-5 py-3 md:text-center text-base font-bold cursor-pointer ${
      selectedTab === type
        ? 'border-leagues-500 text-leagues-500'
        : 'text-gray-500 hover:border-leagues-300 hover:text-leagues-500'
    }`;

  return (
    <Modal open={modalOpened} onOpenChange={() => setIsModalOpen(false)}>
      <ModalContent className='flex h-[500px] w-[calc(100%-16px)] flex-col gap-4 !p-6 !outline-none md:h-[650px] md:max-w-[500px]'>
        <>
          <div className='sticky top-0 flex flex-col gap-4 bg-white'>
            <div className='relative'>
              <h2 className='pr-4 text-left text-lg font-medium leading-[22px] text-gray-900'>
                {`Session ${
                  selectedTab === 'current' && sessionsToShow.length === 1
                    ? sessionsToShow[0]?.SessionNumber
                    : ''
                } - All Game Days`}
              </h2>
              <div
                className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
              </div>
            </div>
            <div className='flex w-full justify-center'>
              <div className='flex w-full max-w-md bg-white pt-1 text-base font-bold'>
                {allSessionsLinks.map((link, i) => (
                  <div
                    key={`${link.type}-${i}`}
                    className={getLinkClass(link.type)}
                    onClick={() => {
                      setSelectedTab(link.type as 'current' | 'past');
                    }}
                  >
                    {link.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className='flex m-auto'>
              <FontAwesomeIcon icon={faSpinnerThird} spin size='2x' />
            </div>
          ) : (
            <div className='flex w-full flex-col justify-between overflow-auto'>
              {sessionsToShow.length >= 1 ? (
                sessionsToShow.map((session, sessionIndex) => {
                  // Determine if collapsible logic should be applied
                  const isCollapsible =
                    selectedTab === 'past' ||
                    (selectedTab === 'current' && sessionsToShow.length > 1);
                  // Determine if this session should be open
                  const shouldShowGameDays =
                    !isCollapsible || isSessionOpened(session, sessionIndex);

                  return (
                    <div key={`session-${session.LeagueSessionID}`}>
                      {isCollapsible && (
                        <div
                          className={`sticky top-0 my-1 flex cursor-pointer flex-row justify-between border-b bg-white ${
                            isSessionOpened(session, sessionIndex)
                              ? 'border-b-leagues-500'
                              : 'border-b-gray-300'
                          } p-2 font-bold text-leagues-500 shadow-lg md:p-3 lg:p-3`}
                          onClick={() => toggleSession(sessionIndex)}
                        >
                          <h2>{`Session ${session.SessionNumber}`}</h2>
                          <h2 className='flex items-center justify-center text-sm font-medium'>
                            {`${session.TotalMatchDates} total game days`}
                            <FontAwesomeIcon
                              icon={
                                isSessionOpened(session, sessionIndex) ? faAngleUp : faAngleDown
                              }
                              className='ml-2 text-leagues-500'
                              size='lg'
                            />
                          </h2>
                        </div>
                      )}

                      {(!isCollapsible || shouldShowGameDays) && (
                        <div>
                          {session.gameDays.map((gameDay) => (
                            <CustomLink
                              href={`${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${gameDay.LeagueSessionID}/matchdate/${gameDay.MatchDateID}?tab=rsvp`}
                              key={`gameDay-${gameDay.MatchDateID}`}
                            >
                              <article
                                className={`my-1 rounded-full border-b bg-gray-50 p-2 text-gray-900 shadow-md hover:bg-leagues-300/50 md:p-3 ${
                                  matchDateId?.toLowerCase() === gameDay.MatchDateID.toLowerCase()
                                    ? 'bg-leagues-300/50'
                                    : ''
                                }`}
                                onClick={() => {
                                  if (
                                    matchDateId?.toLowerCase() !== gameDay.MatchDateID.toLowerCase()
                                  ) {
                                    setLoading(true);
                                  }
                                  setIsModalOpen(false);
                                }}
                              >
                                <div className='flex flex-col-reverse items-start sm:flex-row sm:gap-4'>
                                  <div className='w-full'>
                                    <div className='flex justify-between text-sm md:text-base'>
                                      <h2 className='font-medium'>{gameDay.Title}</h2>
                                      <div className='mr-4 flex flex-1 flex-col items-end'>
                                        {gameDay.DateLockScores && (
                                          <FontAwesomeIcon
                                            icon={faCheck}
                                            className='text-success-500'
                                            size='lg'
                                          />
                                        )}
                                      </div>
                                      <h2 className='font-normal'>
                                        {moment.utc(gameDay.FromDateTime).format('h:mm A')}
                                      </h2>
                                    </div>{' '}
                                  </div>
                                </div>
                              </article>
                            </CustomLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className='text-center'>No {selectedTab} sessions available.</div>
              )}
            </div>
          )}
        </>
      </ModalContent>
    </Modal>
  );
};

export default AllGameDaysModal;
